import { Component, OnInit, HostListener } from '@angular/core';
import { FooterComponent } from "../static/footer/footer.component";
import { HelpandsupportComponent } from "../static/helpandsupport/helpandsupport.component";
import { ClientsComponent } from "../static/clients/clients.component";
import { HeaderComponent } from '../static/header/header.component';
import { AccessibilityGuidanceComponent } from '../static/accessibility-guidance/accessibility-guidance.component';
import { RegularityComponent } from "../static/regularity/regularity.component";
import { LinkPagesComponent } from "../static/link-pages/link-pages.component";
import { HeroSectionComponent } from '../hero-section/hero-section.component';
import { FeaturesSectionComponent } from '../features-section/features-section.component';
import { RiskSectionComponent } from '../risk-section/risk-section.component';

@Component({
  selector: 'app-static-page',
  standalone: true,
  imports: [
    FooterComponent, 
    HelpandsupportComponent, 
    ClientsComponent, 
    HeaderComponent, 
    AccessibilityGuidanceComponent, 
    RegularityComponent, 
    LinkPagesComponent,
    HeroSectionComponent,
    FeaturesSectionComponent,
    RiskSectionComponent
  ],
  templateUrl: './static-page.component.html',
  styleUrl: './static-page.component.scss'
})
export class StaticPageComponent implements OnInit {

  isMobileMenuOpen = false;

  ngOnInit() {
    // Initialize scroll animations using Intersection Observer
    this.initializeScrollAnimations();
    
    // Ensure proper scrolling behavior
    this.ensureScrollBehavior();
    
    // Initialize card scrolling
    this.initializeCardScrolling();
    
    // Set home button as active by default
    this.updateActiveNavLink('home');
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      if (window.scrollY > 50) {
        navbar.classList.add('scrolled');
      } else {
        navbar.classList.remove('scrolled');
      }
    }
    
    // Update active navigation link based on scroll position
    this.updateActiveNavOnScroll();
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    // Re-apply scroll behavior on window resize
    setTimeout(() => {
      this.ensureScrollBehavior();
    }, 100);
  }

  private initializeScrollAnimations() {
    // Main scroll animation observer
    const observerOptions = {
      threshold: [0.05, 0.1, 0.2, 0.3, 0.5],
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const delay = entry.target.getAttribute('data-delay') || '0';
          const delayMs = parseFloat(delay) * 1000;
          
          setTimeout(() => {
            entry.target.classList.add('animate-in');
          }, delayMs);
        } else {
          // Reset animation when element goes out of view
          entry.target.classList.remove('animate-in');
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll(`
      .feature-card, .client-card, .support-card, .hero-title, 
      .hero-text, .hero-image-section, .nav-logo, .nav-links, 
      .nav-auth, .particle, .floating-shapes .shape,
      .section-header, .modern-section-title, .footer-column,
      .contact-section, .footer-links li, .contact-item,
      .risk-text, .risk-title, .risk-description, .risk-features,
      .risk-feature, .feature-icon-small, .risk-visual,
      .additional-cards, .info-card, .card-header, .card-icon,
      .info-card h3, .info-card p
    `);
    
    animatedElements.forEach((el, index) => {
      el.setAttribute('data-delay', (index * 0.1).toString());
      observer.observe(el);
    });

    // Special observer for hero section elements
    const heroObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('hero-animate');
        }
      });
    }, { threshold: 0.5, rootMargin: '0px 0px -200px 0px' });

    // Observe hero elements
    document.querySelectorAll('.hero-title span, .hero-image-section, .particles-container').forEach(el => {
      heroObserver.observe(el);
    });

    // Staggered animation observer for cards and lists
    const staggeredObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const children = entry.target.children;
          Array.from(children).forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('stagger-animate');
            }, index * 150);
          });
        }
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -50px 0px' });

    // Observe containers with multiple children
    document.querySelectorAll('.cards-container, .footer-links, .contact-sections-container').forEach(el => {
      staggeredObserver.observe(el);
    });

    // Parallax effect observer
    const parallaxObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('parallax-active');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -200px 0px' });

    // Observe parallax elements
    document.querySelectorAll('.floating-shapes, .morphing-overlay, .hero-background').forEach(el => {
      parallaxObserver.observe(el);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');
    
    if (mobileMenuToggle && mobileMenu) {
      if (mobileMenuToggle.contains(target)) {
        this.toggleMobileMenu();
      } else if (!mobileMenu.contains(target)) {
        this.closeMobileMenu();
      }
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    const toggle = document.querySelector('.mobile-menu-toggle');
    const menu = document.querySelector('.mobile-menu');
    
    if (toggle && menu) {
      if (this.isMobileMenuOpen) {
        toggle.classList.add('active');
        menu.classList.add('active');
      } else {
        toggle.classList.remove('active');
        menu.classList.remove('active');
      }
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    const toggle = document.querySelector('.mobile-menu-toggle');
    const menu = document.querySelector('.mobile-menu');
    
    if (toggle && menu) {
      toggle.classList.remove('active');
      menu.classList.remove('active');
    }
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      // Get navbar height for offset
      const navbar = document.querySelector('.navbar') as HTMLElement;
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      
      // Calculate the target scroll position with offset
      const elementPosition = element.offsetTop - navbarHeight - 20; // 20px additional padding
      
      // Smooth scroll to the target position
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      // Close mobile menu if open
      this.closeMobileMenu();
      
      // Add active state to the clicked nav link
      this.updateActiveNavLink(sectionId);
    }
  }

  navigateToLogin() {
    window.location.href = '/login';
  }

  private updateActiveNavLink(activeSectionId: string) {
    // Remove active class from all nav links
    const allNavLinks = document.querySelectorAll('.nav-link, .mobile-nav-link');
    allNavLinks.forEach(link => {
      link.classList.remove('active');
    });
    
    // Add active class to the links with matching data-section
    const activeLinks = document.querySelectorAll(`[data-section="${activeSectionId}"]`);
    activeLinks.forEach(link => {
      link.classList.add('active');
    });
  }

  private updateActiveNavOnScroll() {
    const sections = ['home', 'services', 'footer'];
    const scrollPosition = window.scrollY + 100; // Offset for navbar
    
    let activeSection = 'home';
    
    sections.forEach(sectionId => {
      const section = document.getElementById(sectionId);
      if (section) {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          activeSection = sectionId;
        }
      }
    });
    
    // Update active nav link
    this.updateActiveNavLink(activeSection);
  }

  private ensureScrollBehavior() {
    // Ensure all sections are properly visible
    const sections = document.querySelectorAll('section');
    sections.forEach((section, index) => {
      if (section instanceof HTMLElement) {
        section.style.position = 'relative';
        section.style.zIndex = (index + 1).toString();
        section.style.display = 'block';
        section.style.width = '100%';
        section.style.visibility = 'visible';
        section.style.opacity = '1';
      }
    });

    // Ensure proper container behavior
    const containers = document.querySelectorAll('.container');
    containers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.style.maxWidth = '1200px';
        container.style.margin = '0 auto';
        container.style.padding = '0 20px';
        container.style.width = '100%';
        container.style.visibility = 'visible';
        container.style.opacity = '1';
      }
    });

    // Fix any overflow issues
    const modernLandingPage = document.querySelector('.modern-landing-page');
    if (modernLandingPage instanceof HTMLElement) {
      modernLandingPage.style.overflowX = 'hidden';
      modernLandingPage.style.position = 'relative';
      modernLandingPage.style.zIndex = '1';
      modernLandingPage.style.visibility = 'visible';
      modernLandingPage.style.opacity = '1';
    }

    // Ensure all animated elements are visible after animation
    setTimeout(() => {
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach(element => {
        if (element instanceof HTMLElement) {
          element.style.visibility = 'visible';
          element.style.opacity = '1';
        }
      });
    }, 2000);

        // Ensure cards are properly sized and visible
    setTimeout(() => {
      const cards = document.querySelectorAll('.testimonial-card');
      const cardContainers = document.querySelectorAll('.cards-container');
      
      cards.forEach(card => {
        if (card instanceof HTMLElement) {
          card.style.width = '100%';
          card.style.visibility = 'visible';
          card.style.opacity = '1';
        }
      });
      
      cardContainers.forEach(container => {
        if (container instanceof HTMLElement) {
          container.style.width = '100%';
          container.style.maxWidth = '1200px';
          container.style.margin = '0 auto';
          container.style.display = 'grid';
          container.style.gridTemplateColumns = 'repeat(3, 1fr)';
          container.style.gap = '2rem';
        }
      });
    }, 1000);
  }

  private initializeCardScrolling() {
    // Ensure the cards grid is properly set up for static display
    setTimeout(() => {
      const cardsGrid = document.querySelector('.testimonial-cards-grid');
      if (cardsGrid instanceof HTMLElement) {
        // Ensure the grid has proper dimensions
        cardsGrid.style.width = '100%';
        cardsGrid.style.overflow = 'visible';
        cardsGrid.style.display = 'block';
        
        // Set the container for grid layout
        const containers = document.querySelectorAll('.cards-container');
        containers.forEach(container => {
          if (container instanceof HTMLElement) {
            container.style.width = '100%';
            container.style.maxWidth = '1200px';
            container.style.margin = '0 auto';
            container.style.display = 'grid';
            container.style.gridTemplateColumns = 'repeat(3, 1fr)';
            container.style.gap = '2rem';
          }
        });
      }
    }, 1500);
  }
}
